// ==========================================
// UPDATED AuthContext.js
// Anonymous/guest users now get a real JWT from /auth/guest,
// so all read-only API calls succeed without 401 errors.
// ==========================================
import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';

export const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Restore session on mount ──────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // ── Real user login ───────────────────────────────────────────────────────
  const login = useCallback((userData) => {
    localStorage.setItem('token', userData.access_token);
    localStorage.setItem('user', JSON.stringify(userData.user));
    localStorage.removeItem('anonymous_mode');
    setUser(userData.user);
  }, []);

  // ── Guest / anonymous login ───────────────────────────────────────────────
  // Calls POST /api/auth/guest → gets a real (but restricted) JWT.
  // The token is stored like any other token, so all fetch calls work.
  const loginAnonymous = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Guest login failed: ${response.status}`);
      }

      const data = await response.json();

      // Store token exactly like a real login
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('anonymous_mode', 'true');

      // Build the guest user object from the response, mark isAnonymous
      const guestUser = {
        ...data.user,
        isAnonymous: true,
      };
      localStorage.setItem('user', JSON.stringify(guestUser));
      setUser(guestUser);
    } catch (err) {
      console.error('Failed to create guest session:', err);
      // Fallback: create a local-only guest user without a token
      // (will still get 401s, but at least the UI renders)
      const fallbackUser = {
        id: null,
        username: 'Guest',
        email: null,
        isAnonymous: true,
        bio: null,
        profile_picture: null,
        interests: [],
      };
      localStorage.setItem('anonymous_mode', 'true');
      localStorage.setItem('user', JSON.stringify(fallbackUser));
      setUser(fallbackUser);
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('anonymous_mode');
    setUser(null);
  }, []);

  const isAnonymous = user?.isAnonymous === true;

  return (
    <AuthContext.Provider value={{ user, login, loginAnonymous, logout, loading, isAnonymous }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};